import {Skeleton, Typography, useTheme} from '@mui/material';
import React from 'react';
import {differenceInDays, parse} from 'date-fns';
import {Carousel} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {DateFormatEnum, FeaturesEnum} from 'presentation/constants';
import {CarrouselTypeEnum} from 'presentation/constants/carrouselTypeEnum';
import {useContractStatus, useHostName, usePersonalData} from 'presentation/hooks';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {useElectronicBillSubscription} from 'presentation/pages/private/hooks/useElectronicBillSubscription';
import {translations} from 'presentation/translations-new';
import {formatText, getOnlyFirstWord} from 'presentation/utils';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGetHomeQuery} from '../../query/useGetHomeQuery';
import {StyledNotificationsContainer} from '../../styles';
import {AppointmentsHomeStateType} from '../../types';
import BusinessAccessCard from '../BusinessAccessCard';
import CancelledCard from '../CancelledCard';
import CardIllustration from '../CardIllustration';
import NotificationsEmptySvg from '../CardIllustration/NotificationsEmptySvg';
import DigitalInvoiceCard from '../DigitalInvoiceCard';
import {LoadErrorOrEmptyAlert} from '../LoadErrorOrEmptyAlert';
import {SectionContainer} from '../SectionContainer';
import SuspendedCard from '../SuspendedCard';
import {AppointmentsNotifications} from './AppointmentsNotifications';
import {GracePeriodNotifications} from './GracePeriodNotifications';
import {ResponsibleWithoutBenefitsCard} from './ResponsibleWithoutBenefitsCard';
import {StyledBox, StyledLoadingStack} from './styles';
import {RemindersAndNotificationsProps} from './types';
import {MyReferralsCard} from '../MyReferralsCard';
import {FacialBiometricsCard} from '../FacialBiometricsCard';
import {useFacialBiometricFeatureFlag} from 'presentation/pages/private/hooks/useFacialBiometricFeatureFlag';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {isAppointmentPastDue} from '../../utils/isAppointmentPastDue';
import {EarlyAccessCard} from './EarlyAccessCard';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';

const {loadError, loading} = translations['pt-br'].commons;
const {
	remindersAndNotifications,
	remindersAndNotificationsEmpty,
	remindersAndNotificationsLoadError,
} = translations['pt-br'].privateFlow.home;

export function RemindersAndNotifications({isInGracePeriod}: RemindersAndNotificationsProps) {
	const {isLaptop, isMobile} = useMediaQueries();
	const {isSubscribed, hasFinancialAcess} = useElectronicBillSubscription();
	const {
		isResponsibleWithoutBenefits,
		isHolder,
		isB2BAccess,
		healthOnly,
		healthOdonto,
		isEarlyAccess,
		isIndividualPlan,
		odontoOnly,
	} = useAccountInformation();
	const hasFacialBiometrics = useFacialBiometricsStore(state => state.hasFacialBiometrics);
	const {isNotredameSaude} = useHostName();
	const theme = useTheme();

	const {isEnabled: isMyReferralsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_MEUS_ENCAMINHAMENTOS,
	);

	const {isEnabled: isFacialBiometricEnabled} = useFacialBiometricFeatureFlag();
	const {isEnabled: isNotificationsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_NOTIFICATIONS,
	);
	const {isEnabled: isQuickWinWaitingPeriodEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_QUICK_WIN_WAITING_PERIOD,
	);

	function isAppointmentOverSevenDays(appointment: AppointmentsHomeStateType) {
		return (
			differenceInDays(
				parse(appointment.date, DateFormatEnum.DATE_FORMAT_1, new Date()),
				new Date(),
			) > 7
		);
	}

	const getBoxTitleMargins = (isLaptopValue: boolean) => ({
		titleMarginBottom: isLaptopValue ? 2 : 1,
		titleMarginTop: isLaptopValue ? 1 : 0,
	});
	const {titleMarginBottom, titleMarginTop} = getBoxTitleMargins(isLaptop);

	const {
		accountData: {beneficiary},
	} = usePersonalData();

	const needSubscription =
		!isNotificationsFeatureFlagEnabled &&
		isHolder &&
		!isSubscribed &&
		hasFinancialAcess &&
		isIndividualPlan;

	const {data: appointmentsData, isError, isLoading} = useGetHomeQuery();

	const {isValid, isContractSuspended} = useContractStatus();

	const myReferrals = appointmentsData?.myReferrals;

	const showMyReferrals =
		!isNotificationsFeatureFlagEnabled &&
		isValid &&
		!isContractSuspended &&
		!isResponsibleWithoutBenefits &&
		isMyReferralsFeatureFlagEnabled &&
		(healthOnly || healthOdonto) &&
		myReferrals;

	const showFacialBiometricsCard =
		isFacialBiometricEnabled && !hasFacialBiometrics && !odontoOnly;

	if (!isValid || isB2BAccess) {
		return (
			<StyledNotificationsContainer>
				<SectionContainer
					showTitle
					customTitleColor={isLaptop ? theme.colors.brand.primary.white : undefined}
					title={remindersAndNotifications}
					titleMarginBottom={titleMarginBottom}
					titleMarginTop={titleMarginTop}>
					{!isValid ? (
						<CancelledCard
							isResponsibleWithoutBenefits={isResponsibleWithoutBenefits}
						/>
					) : (
						<BusinessAccessCard />
					)}
				</SectionContainer>
			</StyledNotificationsContainer>
		);
	}

	const notificationsDataResponsibleWithout = isContractSuspended
		? [
				!isNotificationsFeatureFlagEnabled ? <SuspendedCard key={'suspended'} /> : null,
				<ResponsibleWithoutBenefitsCard key={'without-benefits'} />,
			]
		: [
				<ResponsibleWithoutBenefitsCard key={'without-benefits'} />,
				needSubscription ? <DigitalInvoiceCard key={'invoice'} /> : null,
			].filter(Boolean);

	const isResponsibleWithoutBenefitsNotificationsSingleItem =
		notificationsDataResponsibleWithout.length === 1;

	if (isResponsibleWithoutBenefits) {
		return (
			<StyledNotificationsContainer>
				<SectionContainer
					showTitle
					titleMarginBottom={titleMarginBottom}
					titleMarginTop={titleMarginTop}
					customTitleColor={isLaptop ? theme.colors.brand.primary.white : undefined}
					title={remindersAndNotifications}>
					<StyledBox
						$isSingleItem={isResponsibleWithoutBenefitsNotificationsSingleItem}
						$isNotredameSaude={isNotredameSaude && !isMobile}>
						<Carousel
							isDarkTheme={isLaptop}
							hideButtons
							type={CarrouselTypeEnum.DOTS}
							slides={notificationsDataResponsibleWithout}
						/>
					</StyledBox>
				</SectionContainer>
			</StyledNotificationsContainer>
		);
	}

	if (isError) {
		if (isLaptop) return null;

		return (
			<LoadErrorOrEmptyAlert
				type="error"
				title={loadError}
				description={remindersAndNotificationsLoadError}
			/>
		);
	}

	if (isLoading) {
		return (
			<StyledNotificationsContainer>
				{isLaptop ? (
					<SectionContainer
						showTitle
						titleMarginTop={isResponsibleWithoutBenefits ? 0 : 2}
						title={remindersAndNotifications}
						customTitleColor={isLaptop ? theme.colors.brand.primary.white : undefined}>
						<StyledLoadingStack>
							<Loading size={24} sx={{color: theme.palette.other.progressBar}} />
							<Typography variant="body2">{loading}</Typography>
						</StyledLoadingStack>
					</SectionContainer>
				) : (
					<SectionContainer
						showTitle
						title={remindersAndNotifications}
						titleMarginTop={0}>
						<Skeleton sx={{transform: 'initial'}} height={72} />
					</SectionContainer>
				)}
			</StyledNotificationsContainer>
		);
	}

	const slides = appointmentsData?.appointments.filter(
		item =>
			!isAppointmentOverSevenDays(item) &&
			!(item.bookingType === BookingTypeEnum.SURGERY && isAppointmentPastDue(item.date)),
	);

	const isEmpty =
		!appointmentsData?.appointments ||
		appointmentsData?.appointments?.length === 0 ||
		slides?.length === 0;

	const userName = formatText(
		getOnlyFirstWord(beneficiary.nmSocialUsuarioC || beneficiary.nmUsuarioC),
	);
	const emptyNotificationDescription = remindersAndNotificationsEmpty.description.replace(
		'$userName',
		userName,
	);

	const showGracePeriodNotificationDeprecated =
		!isQuickWinWaitingPeriodEnabled && isInGracePeriod;

	const notificationsSlides =
		slides?.map((item, index) => (
			<AppointmentsNotifications appointment={item} key={index} />
		)) ?? [];

	const notifications = [
		showMyReferrals ? <MyReferralsCard key={'my-referrals'} /> : null,
		isB2BAccess ? <BusinessAccessCard key={'businessAccess'} /> : null,
		isContractSuspended && !isNotificationsFeatureFlagEnabled ? (
			<SuspendedCard key={'suspended'} />
		) : null,
		needSubscription ? <DigitalInvoiceCard key={'invoice'} /> : null,
		showFacialBiometricsCard ? <FacialBiometricsCard key={'facial-biometrics'} /> : null,
		...notificationsSlides,
		showGracePeriodNotificationDeprecated ? (
			<GracePeriodNotifications key={'grace-period'} />
		) : null,
		isEarlyAccess ? <EarlyAccessCard key={'early-access'} /> : null,
	].filter(Boolean);

	const isNotificationsEmpty = notifications?.length === 0;
	const shouldHideNotifications = isNotificationsEmpty && isLaptop;

	if (shouldHideNotifications) {
		return null;
	}

	const renderNotification =
		notifications?.length > 0 || !isEmpty
			? notifications
			: [
					<CardIllustration
						key={'empty-card'}
						description={emptyNotificationDescription}
						illustration={<NotificationsEmptySvg />}
						customColorDescription={theme.palette.text.primary}
						customBackgroundColor={
							isNotredameSaude ? theme.colors.gray['05Percent'] : undefined
						}
					/>,
				];

	const isSingleItem = renderNotification?.length === 1;

	return (
		<StyledNotificationsContainer>
			<SectionContainer
				showTitle
				title={remindersAndNotifications}
				titleMarginBottom={isResponsibleWithoutBenefits || !isLaptop ? 1 : 2}
				titleMarginTop={isResponsibleWithoutBenefits || !isLaptop ? 0 : 2}
				customTitleColor={isLaptop ? theme.colors.brand.primary.white : undefined}>
				<StyledBox
					$isSingleItem={isSingleItem}
					$isNotredameSaude={isNotredameSaude && !isMobile}>
					<Carousel
						isDarkTheme={isLaptop}
						hideButtons
						type={CarrouselTypeEnum.DOTS}
						slides={renderNotification}
					/>
				</StyledBox>
			</SectionContainer>
		</StyledNotificationsContainer>
	);
}
